import { useMemo, useState } from 'react';
import { Chess, type Square } from 'chess.js';
import { Board } from '../board/Board';
import { checkPuzzleMove, startPosition } from '../../lib/puzzles/play';
import { puzzleThemeLabel, sortThemesByRelevance } from '../../lib/puzzles/themes';
import type { Puzzle, PuzzleAttempt } from '../../types';

export interface PuzzlePlayerProps {
  puzzle: Puzzle;
  onCompleted: (attempt: PuzzleAttempt) => void;
}

type Phase = 'solving' | 'solved' | 'revealed';

/** SAN des coups restants de la solution, depuis `fen` — pour l'affichage en cas de révélation. */
function remainingSolutionSan(fen: string, moves: string[], fromIndex: number): string[] {
  const chess = new Chess(fen);
  const sans: string[] = [];
  for (const uci of moves.slice(fromIndex)) {
    const from = uci.slice(0, 2);
    const to = uci.slice(2, 4);
    const promotion = uci.length > 4 ? uci.slice(4, 5) : undefined;
    try {
      const move = chess.move({ from: from as Square, to: to as Square, promotion });
      if (!move) break;
      sans.push(move.san);
    } catch {
      break;
    }
  }
  return sans;
}

export function PuzzlePlayer({ puzzle, onCompleted }: PuzzlePlayerProps) {
  const start = useMemo(() => startPosition(puzzle), [puzzle]);
  const [fen, setFen] = useState(start.fen);
  const [lastMove, setLastMove] = useState<{ from: string; to: string } | null>(start.lastMoveSquares);
  const [stepIndex, setStepIndex] = useState(1);
  const [phase, setPhase] = useState<Phase>('solving');
  const [message, setMessage] = useState<string | null>(null);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [hadWrongAttempt, setHadWrongAttempt] = useState(false);
  const [completed, setCompleted] = useState(false);

  const sortedThemes = useMemo(() => sortThemesByRelevance(puzzle.themes), [puzzle.themes]);
  const theme = sortedThemes[0];
  const hintSquare = hintsUsed >= 2 ? puzzle.moves[stepIndex]?.slice(0, 2) : undefined;

  function complete(solvedFirstTry: boolean, revealed: boolean) {
    if (completed) return;
    setCompleted(true);
    onCompleted({
      id: `${puzzle.id}-${Date.now()}`,
      puzzleId: puzzle.id,
      timestamp: new Date().toISOString(),
      themes: puzzle.themes,
      puzzleRating: puzzle.rating,
      solvedFirstTry,
      hintsUsed,
      revealed,
    });
  }

  function handleMove(uci: string) {
    if (phase !== 'solving') return;
    const result = checkPuzzleMove(puzzle, fen, uci, stepIndex);
    if (!result) return; // coup illégal : ne devrait pas arriver, Board ne propose que des coups légaux
    if (!result.correct) {
      setHadWrongAttempt(true);
      setMessage('Pas le bon coup. Réessaie.');
      return;
    }
    setMessage(null);
    if (result.done) {
      setFen(result.fen);
      setLastMove(null);
      setPhase('solved');
      complete(!hadWrongAttempt && hintsUsed === 0, false);
      return;
    }
    setFen(result.fen);
    setLastMove(result.opponentReplySquares ?? null);
    setStepIndex((i) => i + 2);
  }

  function useHint() {
    if (hintsUsed >= 2) return;
    setHintsUsed((h) => h + 1);
  }

  function reveal() {
    setPhase('revealed');
    complete(false, true);
  }

  const solutionSan = phase === 'revealed' ? remainingSolutionSan(fen, puzzle.moves, stepIndex) : [];

  return (
    <div className="exercise-player">
      <div className="chesscom-timeclass-chips" style={{ marginBottom: '0.25rem' }}>
        {sortedThemes.slice(0, 3).map((t) => (
          <span key={t} className="theme-badge">
            {puzzleThemeLabel(t)}
          </span>
        ))}
        <span className="muted small">Elo {puzzle.rating}</span>
      </div>
      <p className="muted">
        {start.playerColor === 'w' ? 'Trait aux Blancs' : 'Trait aux Noirs'} — à toi de trouver le meilleur coup.
      </p>
      <Board
        fen={fen}
        orientation={start.playerColor}
        playableColor={phase === 'solving' ? start.playerColor : undefined}
        onUserMove={phase === 'solving' ? handleMove : undefined}
        lastMoveSquares={lastMove}
        highlightSquares={hintSquare ? [hintSquare] : undefined}
        boardId={`puzzle-${puzzle.id}`}
      />

      {message && (
        <p className="feedback" role="status">
          {message}
        </p>
      )}
      {phase === 'solved' && (
        <p className="feedback success" role="status">
          Résolu !
        </p>
      )}

      {hintsUsed > 0 && phase === 'solving' && (
        <ul className="hints-list">
          <li>Thème : {puzzleThemeLabel(theme)}.</li>
          {hintsUsed >= 2 && <li>La case de départ du coup à jouer est surlignée sur l'échiquier.</li>}
        </ul>
      )}

      {phase === 'revealed' && (
        <div className="explanation">
          <p>
            Solution : <strong>{solutionSan.join(' ')}</strong>
          </p>
          <p className="muted small">
            <a href={`https://lichess.org/training/${puzzle.id}`} target="_blank" rel="noreferrer">
              Voir ce puzzle sur Lichess
            </a>
          </p>
        </div>
      )}
      {phase === 'solved' && (
        <p className="muted small">
          <a href={`https://lichess.org/training/${puzzle.id}`} target="_blank" rel="noreferrer">
            Voir ce puzzle sur Lichess
          </a>
        </p>
      )}

      {phase === 'solving' && (
        <div className="actions">
          {hintsUsed < 2 && (
            <button type="button" className="link-btn" onClick={useHint}>
              Indice ({hintsUsed}/2)
            </button>
          )}
          <button type="button" className="link-btn" onClick={reveal}>
            Voir la solution
          </button>
        </div>
      )}
    </div>
  );
}
